import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
export function BlogDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Mock article data
  const article = {
    id: slug,
    category: 'Gastronomie',
    date: '10 Décembre 2024',
    author: 'ARWM',
    title: 'Marrakech, Capitale de la Gastronomie Marocaine',
    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200',
    content: `
      Marrakech s'affirme de plus en plus comme la capitale incontestée de la gastronomie marocaine. La ville ocre attire chaque année des millions de visiteurs en quête d'expériences culinaires authentiques et raffinées.

      **Un patrimoine culinaire exceptionnel**

      La cuisine marocaine, reconnue mondialement pour sa richesse et sa diversité, trouve à Marrakech son expression la plus aboutie. Des tajines traditionnels aux créations contemporaines, la ville offre un panorama complet de l'art culinaire marocain.

      **L'excellence au service de la tradition**

      Les restaurants membres de l'ARWM incarnent cette excellence. Ils perpétuent les traditions tout en innovant, créant ainsi une scène gastronomique dynamique et attractive. Cette combinaison unique fait de Marrakech une destination de choix pour les amateurs de bonne cuisine.

      **Un écosystème favorable**

      Le développement de la gastronomie à Marrakech s'appuie sur un écosystème favorable : producteurs locaux de qualité, formations professionnelles, soutien des autorités, et une clientèle internationale exigeante.

      **Perspectives d'avenir**

      L'ARWM travaille activement à renforcer cette position de leader. À travers ses actions de promotion, de formation et de représentation, l'association contribue à faire rayonner la gastronomie marocaine bien au-delà des frontières du royaume.
    `,
    relatedArticles: [{
      id: 'nouvelles-normes-hygiene',
      title: "Nouvelles Normes d'Hygiène 2025",
      date: '5 Décembre 2024'
    }, {
      id: 'conseils-gestion-restaurant',
      title: '10 Conseils pour Optimiser Votre Restaurant',
      date: '28 Novembre 2024'
    }]
  };
  useSEO(`${article.title} - Blog ARWM`, article.content.substring(0, 150).trim().replace(/\n/g, ' ') + '...');
  return <div className="w-full" data-id="element-198">
      {/* Hero Image */}
      <div className="h-[400px] bg-cover bg-center relative" style={{
      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${article.image})`
    }} data-id="element-199">
        <div className="max-w-[1280px] mx-auto px-4 h-full flex items-end pb-12" data-id="element-200">
          <Link to="/blog" className="absolute top-8 left-8 flex items-center text-white font-josefin hover:underline" data-id="element-201">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-202" />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Content */}
      <section className="py-20 px-4 bg-white" data-id="element-203">
        <div className="max-w-[1280px] mx-auto" data-id="element-204">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-205">
            {/* Main Content */}
            <div className="lg:col-span-2" data-id="element-206">
              <div className="mb-6" data-id="element-207">
                <span className="inline-block px-4 py-2 bg-arwm-maroon text-arwm-cream font-josefin text-sm uppercase tracking-wider" data-id="element-208">
                  {article.category}
                </span>
              </div>

              <h1 className="font-cormorant text-5xl font-bold text-arwm-dark-text mb-6" data-id="element-209">
                {article.title}
              </h1>

              <div className="flex items-center gap-6 mb-8 font-josefin text-arwm-dark-text" data-id="element-210">
                <div className="flex items-center" data-id="element-211">
                  <Calendar className="w-5 h-5 mr-2 text-arwm-maroon" data-id="element-212" />
                  <span data-id="element-213">{article.date}</span>
                </div>
                <div className="flex items-center" data-id="element-214">
                  <User className="w-5 h-5 mr-2 text-arwm-maroon" data-id="element-215" />
                  <span data-id="element-216">{article.author}</span>
                </div>
              </div>

              <div className="prose prose-lg max-w-none font-josefin text-arwm-dark-text" data-id="element-217">
                {article.content.split('\n\n').map((paragraph, index) => {
                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                  return <h2 key={index} className="font-cormorant text-3xl font-bold text-arwm-maroon mt-8 mb-4" data-id="element-218">
                        {paragraph.replace(/\*\*/g, '')}
                      </h2>;
                }
                return <p key={index} className="mb-6 leading-relaxed" data-id="element-219">
                      {paragraph}
                    </p>;
              })}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1" data-id="element-220">
              <Card data-id="element-221">
                <CardHeader data-id="element-222">
                  <CardTitle className="font-cormorant text-2xl" data-id="element-223">
                    Articles récents
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-224">
                  <div className="space-y-6" data-id="element-225">
                    {article.relatedArticles.map(related => <div key={related.id} data-id="element-226">
                        <Link to={`/blog-posts/${related.id}`} className="group" data-id="element-227">
                          <p className="font-josefin text-sm text-arwm-maroon mb-2" data-id="element-228">
                            {related.date}
                          </p>
                          <h3 className="font-cormorant text-lg font-bold text-arwm-dark-text group-hover:text-arwm-maroon transition-colors" data-id="element-229">
                            {related.title}
                          </h3>
                        </Link>
                      </div>)}
                  </div>
                  <Link to="/blog" className="inline-block mt-6 font-josefin text-arwm-maroon text-sm font-semibold hover:underline" data-id="element-230">
                    Voir tous les articles →
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>;
}